import { useEffect, useState } from "react";
import { SUPPORT_EMAIL_SCHEMA } from "@/lib/enquiries/rules";
import { createFileRoute } from "@tanstack/react-router";
import { useAdminSettings, SUPPORTED_CURRENCIES, type AdminSettings } from "@/lib/admin/settings";
import { PageHeader } from "@/components/admin/PageHeader";
import { logAction } from "@/lib/admin/audit/log";
import { notify } from "@/lib/admin/notify";
import { ShippingPartnersSection } from "@/components/admin/ShippingPartners";
import { WhatsAppChatSettingsSection } from "@/components/admin/WhatsAppChatSettingsSection";
import { resolveWhatsAppChatSettings, validateWhatsAppChat } from "@/lib/whatsapp-chat/config";
import { INDIAN_STATES } from "@/lib/tax/gst";
import { HomePromotionsSettingsSection } from "@/components/admin/HomePromotionsSettingsSection";
import { PaymentModeSection } from "@/components/admin/PaymentModeSection";
import { resolveHomeAnnouncement, resolveHomePromo, validateHomePromo } from "@/lib/home-promo/config";


/** Tax rates must be numeric and never negative; 0 is allowed. */
function rateValue(input: string): number {
  const n = Number(input);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

const TABS = ["Brand", "Localisation", "Address", "Contact", "Tax", "Shipping", "Shipping partners", "WhatsApp Chat", "Homepage / Promotions", "SEO", "Social", "Legal", "Payments"] as const;
type Tab = typeof TABS[number];

/** URL-safe slug per tab, so the sidebar can deep-link (?tab=homepage-promotions). */
const tabSlug = (t: Tab) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const tabFromSlug = (slug: string | undefined): Tab =>
  TABS.find((t) => tabSlug(t) === slug) ?? "Brand";

export const Route = createFileRoute("/admin/settings")({
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search.tab === "string" && search.tab ? { tab: search.tab } : {},
  component: SettingsPage,
});

function SettingsPage() {
  const [settings, save] = useAdminSettings();
  const [draft, setDraft] = useState<AdminSettings>(settings);
  const { tab: tabParam } = Route.useSearch();
  const navigate = Route.useNavigate();
  const tab = tabFromSlug(tabParam);
  const setTab = (t: Tab) =>
    void navigate({ search: t === "Brand" ? {} : { tab: tabSlug(t) }, replace: true });
  useEffect(() => { setDraft(settings); }, [settings]);
  const whatsappChat = resolveWhatsAppChatSettings(draft.whatsappChat, draft.contact?.whatsapp);
  const homeAnnouncement = resolveHomeAnnouncement(draft.homeAnnouncement);
  const homePromo = resolveHomePromo(draft.homePromo);

  const [saving, setSaving] = useState(false);
  const commit = async () => {
    if (draft.whatsappChat) {
      const problems = validateWhatsAppChat(whatsappChat);
      if (problems.length) {
        setTab("WhatsApp Chat");
        notify.error("Check WhatsApp chat settings", problems[0]);
        return;
      }
    }
    if (draft.homePromo) {
      const problems = validateHomePromo(homePromo);
      if (problems.length) {
        setTab("Homepage / Promotions");
        notify.error("Check the promotional banner", problems[0]);
        return;
      }
    }
    const supportCheck = SUPPORT_EMAIL_SCHEMA.safeParse(draft.contact.email);
    if (!supportCheck.success) {
      setTab("Contact");
      notify.error("Check the support email", "Enter a valid support email address.");
      return;
    }
    setSaving(true);
    try {
      // One editor (Contact tab); the legacy supportEmail key mirrors it.
      await save({ ...draft, contact: { ...draft.contact, email: supportCheck.data }, supportEmail: supportCheck.data });
      logAction({ entity: "setting", action: "update", summary: `Updated store settings (${tab})` });
      notify.success("Settings saved", "Storefront updated immediately.");
    } catch (err) {
      notify.error("Could not save settings", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Store settings" subtitle="Business, commerce, and marketing configuration" action={
        <button onClick={() => void commit()} disabled={saving} className="border border-black bg-black px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
      }/>

      <div className="flex flex-wrap gap-1 border-b border-black/10">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-widest ${tab === t ? "border-b-2 border-black text-black" : "text-black/50 hover:text-black"}`}>{t}</button>
        ))}
      </div>

      <div className="border border-black/10 bg-white p-4">
        {tab === "Brand" && <Grid>
          <Field label="Store name" value={draft.storeName} onChange={(v) => setDraft({ ...draft, storeName: v })}/>
          <Field label="Legal name" value={draft.legalName} onChange={(v) => setDraft({ ...draft, legalName: v })}/>
          <Field label="Tagline" value={draft.brandTagline} onChange={(v) => setDraft({ ...draft, brandTagline: v })}/>
          <Field label="Logo URL" value={draft.logoUrl} onChange={(v) => setDraft({ ...draft, logoUrl: v })}/>
          <Field label="Accent colour" value={draft.branding.accentColor} onChange={(v) => setDraft({ ...draft, branding: { ...draft.branding, accentColor: v } })}/>
        </Grid>}
        {tab === "Localisation" && <Grid>
          <label className="grid gap-1"><span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">Currency</span>
            <select value={draft.currency} onChange={(e) => {
              const cur = SUPPORTED_CURRENCIES.find((c) => c.code === e.target.value);
              setDraft({ ...draft, currency: e.target.value, locale: cur?.locale ?? draft.locale });
            }} className="border border-black/15 px-2 py-2">
              {SUPPORTED_CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select></label>
          <Field label="Locale" value={draft.locale} onChange={(v) => setDraft({ ...draft, locale: v })}/>
          <Field label="Timezone" value={draft.timezone} onChange={(v) => setDraft({ ...draft, timezone: v })}/>
        </Grid>}
        {tab === "Address" && <Grid>
          <Field label="Line 1" value={draft.address.line1} onChange={(v) => setDraft({ ...draft, address: { ...draft.address, line1: v } })}/>
          <Field label="Line 2" value={draft.address.line2 ?? ""} onChange={(v) => setDraft({ ...draft, address: { ...draft.address, line2: v } })}/>
          <Field label="City" value={draft.address.city} onChange={(v) => setDraft({ ...draft, address: { ...draft.address, city: v } })}/>
          <Field label="State" value={draft.address.state} onChange={(v) => setDraft({ ...draft, address: { ...draft.address, state: v } })}/>
          <Field label="Postal code" value={draft.address.postalCode} onChange={(v) => setDraft({ ...draft, address: { ...draft.address, postalCode: v } })}/>
          <Field label="Country" value={draft.address.country} onChange={(v) => setDraft({ ...draft, address: { ...draft.address, country: v } })}/>
        </Grid>}
        {tab === "Contact" && <Grid>
          <Field label="Phone" value={draft.contact.phone} onChange={(v) => setDraft({ ...draft, contact: { ...draft.contact, phone: v } })}/>
          <Field label="WhatsApp" value={draft.contact.whatsapp ?? ""} onChange={(v) => setDraft({ ...draft, contact: { ...draft.contact, whatsapp: v } })}/>
          <Field label="Support email (shown on the website; receives Contact enquiries)" value={draft.contact.email} onChange={(v) => setDraft({ ...draft, contact: { ...draft.contact, email: v } })}/>
          <Field label="Hours (Mon–Fri)" value={draft.hours.weekdays} onChange={(v) => setDraft({ ...draft, hours: { ...draft.hours, weekdays: v } })}/>
          <Field label="Hours (Saturday)" value={draft.hours.saturday} onChange={(v) => setDraft({ ...draft, hours: { ...draft.hours, saturday: v } })}/>
          <Field label="Hours (Sunday)" value={draft.hours.sunday} onChange={(v) => setDraft({ ...draft, hours: { ...draft.hours, sunday: v } })}/>
        </Grid>}
        {tab === "Tax" && <Grid>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.tax.enabled} onChange={(e) => setDraft({ ...draft, tax: { ...draft.tax, enabled: e.target.checked } })}/> GST enabled</label>
          <Field label="Label" value={draft.tax.label} onChange={(v) => setDraft({ ...draft, tax: { ...draft.tax, label: v } })}/>
          <Field label="CGST rate (%) — confirm with your CA" value={String(draft.tax.cgstRatePct ?? 0)} onChange={(v) => setDraft({ ...draft, tax: { ...draft.tax, cgstRatePct: rateValue(v) } })}/>
          <Field label="SGST rate (%) — confirm with your CA" value={String(draft.tax.sgstRatePct ?? 0)} onChange={(v) => setDraft({ ...draft, tax: { ...draft.tax, sgstRatePct: rateValue(v) } })}/>
          <Field label="IGST rate (%) — confirm with your CA" value={String(draft.tax.igstRatePct ?? 0)} onChange={(v) => setDraft({ ...draft, tax: { ...draft.tax, igstRatePct: rateValue(v) } })}/>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.tax.inclusive} onChange={(e) => setDraft({ ...draft, tax: { ...draft.tax, inclusive: e.target.checked } })}/> Prices are tax-inclusive</label>
          <Field label="GSTIN" value={draft.tax.gstin ?? ""} onChange={(v) => setDraft({ ...draft, tax: { ...draft.tax, gstin: v.toUpperCase() } })}/>
          <label className="grid gap-1"><span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">Business state (place of supply origin)</span>
            <select value={draft.tax.businessState ?? "Maharashtra"} onChange={(e) => setDraft({ ...draft, tax: { ...draft.tax, businessState: e.target.value } })} className="border border-black/15 px-2 py-2">
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select></label>
          <label className="grid gap-1"><span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">Effective from</span>
            <input type="date" value={draft.tax.effectiveFrom ?? ""} onChange={(e) => setDraft({ ...draft, tax: { ...draft.tax, effectiveFrom: e.target.value } })} className="border border-black/15 px-2 py-2 text-sm"/></label>
          <Field label="Default HSN / tax classification" value={draft.tax.defaultHsn ?? ""} onChange={(v) => setDraft({ ...draft, tax: { ...draft.tax, defaultHsn: v } })}/>
          <p className="md:col-span-2 text-[11px] text-black/50">Maharashtra customers are charged CGST + SGST. Customers in other Indian states are charged IGST. International orders are not taxed in this phase.</p>
        </Grid>}

        {tab === "Shipping" && <Grid>
          <Field label="Free threshold" value={String(draft.shipping.freeThreshold)} onChange={(v) => setDraft({ ...draft, shipping: { ...draft.shipping, freeThreshold: Number(v) || 0 } })}/>
          <Field label="Standard fee" value={String(draft.shipping.standardFee)} onChange={(v) => setDraft({ ...draft, shipping: { ...draft.shipping, standardFee: Number(v) || 0 } })}/>
          <Field label="Express fee" value={String(draft.shipping.expressFee)} onChange={(v) => setDraft({ ...draft, shipping: { ...draft.shipping, expressFee: Number(v) || 0 } })}/>
          <Field label="Standard ETA" value={draft.shipping.standardEtaDays} onChange={(v) => setDraft({ ...draft, shipping: { ...draft.shipping, standardEtaDays: v } })}/>
          <Field label="Express ETA" value={draft.shipping.expressEtaDays} onChange={(v) => setDraft({ ...draft, shipping: { ...draft.shipping, expressEtaDays: v } })}/>
        </Grid>}
        {tab === "Shipping partners" && <ShippingPartnersSection />}
        {tab === "WhatsApp Chat" && (
          <WhatsAppChatSettingsSection
            value={whatsappChat}
            onChange={(next) => setDraft({ ...draft, whatsappChat: next })}
            onSave={() => void commit()}
            saving={saving}
          />
        )}
        {tab === "Homepage / Promotions" && (
          <HomePromotionsSettingsSection
            announcement={homeAnnouncement}
            promo={homePromo}
            onAnnouncementChange={(next) => setDraft({ ...draft, homeAnnouncement: next })}
            onPromoChange={(next) => setDraft({ ...draft, homePromo: next })}
            onSave={() => void commit()}
            saving={saving}
          />
        )}
        {tab === "SEO" && <Grid>
          <Field label="Meta title" value={draft.seo.metaTitle} onChange={(v) => setDraft({ ...draft, seo: { ...draft.seo, metaTitle: v } })}/>
          <Field label="Meta description" value={draft.seo.metaDescription} onChange={(v) => setDraft({ ...draft, seo: { ...draft.seo, metaDescription: v } })}/>
          <Field label="Keywords" value={draft.seo.keywords} onChange={(v) => setDraft({ ...draft, seo: { ...draft.seo, keywords: v } })}/>
          <Field label="OG image URL" value={draft.seo.ogImage} onChange={(v) => setDraft({ ...draft, seo: { ...draft.seo, ogImage: v } })}/>
        </Grid>}
        {tab === "Social" && <Grid>
          <Field label="Instagram" value={draft.social.instagram ?? ""} onChange={(v) => setDraft({ ...draft, social: { ...draft.social, instagram: v } })}/>
          <Field label="Facebook" value={draft.social.facebook ?? ""} onChange={(v) => setDraft({ ...draft, social: { ...draft.social, facebook: v } })}/>
          <Field label="X" value={draft.social.x ?? ""} onChange={(v) => setDraft({ ...draft, social: { ...draft.social, x: v } })}/>
          <Field label="YouTube" value={draft.social.youtube ?? ""} onChange={(v) => setDraft({ ...draft, social: { ...draft.social, youtube: v } })}/>
        </Grid>}
        {tab === "Payments" && <PaymentModeSection />}
        {tab === "Legal" && <Grid>
          <Field label="GSTIN" value={draft.legal.gstin ?? ""} onChange={(v) => setDraft({ ...draft, legal: { ...draft.legal, gstin: v } })}/>
          <Field label="CIN" value={draft.legal.cin ?? ""} onChange={(v) => setDraft({ ...draft, legal: { ...draft.legal, cin: v } })}/>
          <Field label="PAN" value={draft.legal.pan ?? ""} onChange={(v) => setDraft({ ...draft, legal: { ...draft.legal, pan: v } })}/>
          <Field label="Terms URL" value={draft.legal.termsUrl} onChange={(v) => setDraft({ ...draft, legal: { ...draft.legal, termsUrl: v } })}/>
          <Field label="Privacy URL" value={draft.legal.privacyUrl} onChange={(v) => setDraft({ ...draft, legal: { ...draft.legal, privacyUrl: v } })}/>
          <Field label="Refunds URL" value={draft.legal.refundUrl} onChange={(v) => setDraft({ ...draft, legal: { ...draft.legal, refundUrl: v } })}/>
        </Grid>}
      </div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-3 md:grid-cols-2">{children}</div>;
}
function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="grid gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-2 py-2 text-sm"/>
    </label>
  );
}
